import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { bindActionCreators } from 'redux';
import {
  Card,
  CardBody,
  CardTitle,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { Loading } from '../../../../../loadbar/Loading';
import {
  clearMarigramInfo as clearMarigramInfoAc,
  getMarigramInfo as getMarigramInfoAc,
  getMarigramReferences as getMarigramReferencesAc,
  sortReferences as sortReferencesAc,
  toggleColumns as toggleColumnsAC,
} from '../../../../../../redux/tsunamis/marigrams/info/marigramInfoActions';
import { parseAndShortenFloat } from '../../../../../../helperFunctions/helperFunctions';
import { SingleResultGrid } from '../../../../../grid/SingleResultGrid';
import { Column } from '../../../../../grid/Column';
import { ReferencesTable } from '../../../../../table/ReferencesTable';
import { isDataManager } from '../../../../../../role/roleUtils';
import { EditIcon } from '../../../../../icon/EditIcon';

const renderEditLink = id => (
  <Link aria-label={`Edit Marigram ${id}`} to={`/view/hazards/marigram/update-marigram/${id}`} target="_blank" rel="noopener noreferrer">
    <EditIcon />
  </Link>
);
class MarigramInfoPageUnwrapped extends Component {
  constructor(props) {
    super(props);
    this.handleToggleColumns = this.handleToggleColumns.bind(this);
  }

  componentDidMount() {
    document.title = 'NCEI Hazard Marigram Information';
    const {
      match: { params }, getMarigramInfo, getMarigramReferences,
    } = this.props;
    getMarigramReferences({ id: params.id });
    getMarigramInfo({ id: params.id });
  }

  componentWillUnmount() {
    const { clearMarigramInfo } = this.props;
    clearMarigramInfo();
  }

  handleToggleColumns({ visibleColumns, sort }) {
    const {
      visibleColumns: preVisibleColumns, sort: prevSort, sortReferences, toggleColumns,
    } = this.props;
    let changed = false;
    if (!visibleColumns.sort().equals(preVisibleColumns.sort())) {
      toggleColumns(visibleColumns);
      changed = true;
    }
    if (!sort.equals(prevSort)) {
      sortReferences(sort);
      changed = true;
    }
    return changed;
  }

  render() {
    const {
      fetched, tsunami, references, sort, visibleColumns, match: { params: { id } }, authorities,
    } = this.props;

    const dataManager = isDataManager(authorities);
    if (!fetched) {
      return (<Loading />);
    }

    // Example page:
    // https://www.ngdc.noaa.gov/nndc/struts/results?EQ_0=5579&t=101650&s=9&d=99,91,95,93&nd=display
    return (
      <div className="haz-results-container haz-page-container">
        <Card className="haz-page-card">
          <CardBody>
            <CardTitle className="haz-page-card-title">Runup Information</CardTitle>
            {dataManager && renderEditLink(id)}
            <div className="haz-info-content">

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle>Tsunami Event</CardTitle>
                    <SingleResultGrid data={tsunami}>
                      <Column id="year" width={75} helpModalId="year">Year</Column>
                      <Column id="month" width={50} helpModalId="month">Mo</Column>
                      <Column id="day" width={50} helpModalId="day">Dy</Column>
                      <Column id="hour" width={50} helpModalId="hour">Hr</Column>
                      <Column id="minute" width={50} helpModalId="minute">Mn</Column>
                      <Column id="second" width={75} helpModalId="second">Sec</Column>
                      <Column id="sourceEventValidity" width={85} helpModalId="tsuValidity">Tsunami Event Validity</Column>
                      <Column id="sourceCauseCode" width={85} helpModalId="tsuCauseCode">Tsunami Cause Code</Column>
                      <Column id="sourceCountry" width={150} helpModalId="tsuCountry">Tsunami Country</Column>
                      <Column id="sourceLocationName" width={200} helpModalId="tsuLocationName">Location Name</Column>
                      <Column id="sourceLatitude" width={100} helpModalId="tsuLatitude" cell={({ value }) => (<div>{parseAndShortenFloat(value)}</div>)}>Latitude</Column>
                      <Column id="sourceLongitude" width={100} helpModalId="tsuLongitude" cell={({ value }) => (<div>{parseAndShortenFloat(value)}</div>)}>Longitude</Column>
                      <Column id="sourceMaxWaterHeight" helpModalId="tsuMaxWaterHeight" width={125}>Maximum Water Height (m)</Column>
                      <Column
                        id="sourceNumRunups"
                        width={100}
                        helpModalId="tsuRunups"
                        sortable
                        cell={({ value, row }) => (value ? <Link target="_blank" rel="noopener noreferrer" to={{ pathname: '/view/hazards/tsunami/runup-data', search: `?tsunamiEventId=${row.get('tsunamiEventId')}` }}>{value}</Link> : value)}
                      >
                        Number of Runups
                      </Column>
                      <Column id="sourceNumDeposits" width={100} helpModalId="tsuDeposits">Number of Deposits</Column>
                      <Column id="sourceTsMtAbe" width={100} helpModalId="tsuMagAbe">Tsunami Magnitude (Abe)</Column>
                      <Column id="sourceTsMtIi" width={100} helpModalId="tsuMagIida">Tsunami Magnitude (Iida)</Column>
                      <Column id="sourceTsIntensity" width={100} helpModalId="tsuIntensity">Tsunami Intensity</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle>Tsunami Effects</CardTitle>
                    <SingleResultGrid data={tsunami}>
                      <Column id="deaths" width={100} helpModalId="ruDeaths">Deaths</Column>
                      <Column id="deathsAmountOrder" width={125} helpModalId="ruDeathsDescription">Death Description</Column>
                      <Column id="missing" width={100} helpModalId="ruMissing">Missing</Column>
                      <Column id="missingAmountOrder" width={125} helpModalId="ruMissingDescription">Missing Description</Column>
                      <Column id="injuries" width={100} helpModalId="ruInjuries">Injuries</Column>
                      <Column id="injuriesAmountOrder" width={125} helpModalId="ruInjuriesDescription">Injuries Description</Column>
                      <Column id="damageMillionsDollars" width={100} helpModalId="ruDamage">Damage $Mil</Column>
                      <Column id="damageAmountOrder" width={125} helpModalId="ruDamageDescription">Damage Description</Column>
                      <Column id="housesDestroyed" width={100} helpModalId="ruHousesDestroyed">Houses Destroyed</Column>
                      <Column id="housesDestroyedAmountOrder" width={125} helpModalId="ruHousesDestroyedDescription">Houses Destroyed Description</Column>
                      <Column id="housesDamaged" width={100} helpModalId="ruHousesDamaged">Houses Damaged</Column>
                      <Column id="housesDamagedAmountOrder" width={125} helpModalId="ruHousesDamagedDescription">Houses Damaged Description</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle>Total Effects (Tsunami and Source)</CardTitle>
                    <SingleResultGrid data={tsunami}>
                      <Column id="deathsTotal" width={100} helpModalId="tsuTotalDeaths">Total Deaths</Column>
                      <Column id="deathsAmountOrderTotal" width={125} helpModalId="tsuTotalDeathsDescription">Total Death Description</Column>
                      <Column id="missingTotal" width={100} helpModalId="tsuTotalMissing">Total Missing</Column>
                      <Column id="missingAmountOrderTotal" width={125} helpModalId="tsuTotalMissingDescription">Total Missing Description</Column>
                      <Column id="injuriesTotal" width={100} helpModalId="tsuTotalInjuries">Total Injuries</Column>
                      <Column id="injuriesAmountOrderTotal" width={125} helpModalId="tsuTotalInjuriesDescription">Total Injuries Description</Column>
                      <Column id="damageMillionsDollarsTotal" width={100} helpModalId="tsuTotalDamage">Total Damage ($Mil)</Column>
                      <Column id="damageAmountOrderTotal" width={125} helpModalId="tsuTotalDamageDescription">Total Damage Description</Column>
                      <Column id="housesDestroyedTotal" width={100} helpModalId="tsuTotalHousesDestroyed">Total Houses Destroyed</Column>
                      <Column id="housesDestroyedAmountOrderTotal" width={125} helpModalId="tsuTotalHousesDestroyedDescription">Total Houses Destroyed Description</Column>
                      <Column id="housesDamagedTotal" width={100} helpModalId="tsuTotalHousesDamaged">Total Houses Damaged</Column>
                      <Column id="housesDamagedAmountOrderTotal" width={125} helpModalId="tsuTotalHousesDamagedDescription">Total Houses Damaged Description</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section haz-reference-info">
                <Card>
                  <CardBody>
                    <CardTitle>References for the Tsunami Event</CardTitle>
                    <ReferencesTable
                      references={references}
                      handleToggleColumns={this.handleToggleColumns}
                      sort={sort}
                      visibleColumns={visibleColumns}
                    />
                  </CardBody>
                </Card>
              </div>

            </div>


          </CardBody>
        </Card>
      </div>
    );
  }
}


MarigramInfoPageUnwrapped.propTypes = {
  match: PropTypes.shape({ params: PropTypes.shape({ id: PropTypes.string }) }).isRequired,
  fetched: PropTypes.bool.isRequired,
  clearMarigramInfo: PropTypes.func.isRequired,
  getMarigramInfo: PropTypes.func.isRequired,
  getMarigramReferences: PropTypes.func.isRequired,
  // marigram: ImmutablePropTypes.map.isRequired,
  tsunami: ImmutablePropTypes.map.isRequired,
  references: ImmutablePropTypes.list.isRequired,
  sortReferences: PropTypes.func.isRequired,
  sort: ImmutablePropTypes.list.isRequired,
  visibleColumns: ImmutablePropTypes.list.isRequired,
  toggleColumns: PropTypes.func.isRequired,
  authorities: ImmutablePropTypes.list.isRequired,
};

const mapStateToProps = (state) => {
  const fetchedMarigram = state.getIn(['marigramInfoPage', 'fetchedMarigram']);
  const fetchedReferences = state.getIn(['marigramInfoPage', 'fetchedReferences']);
  return {
    fetched: [fetchedMarigram, fetchedReferences].every(v => v),
    runup: state.getIn(['marigramInfoPage', 'marigram']),
    references: state.getIn(['marigramInfoPage', 'references']),
    sort: state.getIn(['marigramInfoPage', 'sort']),
    visibleColumns: state.getIn(['marigramInfoPage', 'visibleColumns']),
    sortReferences: PropTypes.func.isRequired,
    toggleColumns: PropTypes.func.isRequired,
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({
  clearMarigramInfo: clearMarigramInfoAc,
  getMarigramInfo: getMarigramInfoAc,
  getMarigramReferences: getMarigramReferencesAc,
  sortReferences: sortReferencesAc,
  toggleColumns: toggleColumnsAC,
}, dispatch);

export const MarigramInfoPage = connect(mapStateToProps, mapDispatchToProps)(MarigramInfoPageUnwrapped);
